import { baseApi } from './baseApi';

export interface ProductionCenter {
  id: string;
  name: string;
  description?: string;
  printTicket: boolean;
  showOnScreen: boolean;
  printerConfigurationId?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt?: string;
  productCount: number;
}

export interface ProductionCenterSimple {
  id: string;
  name: string;
  printTicket: boolean;
  showOnScreen: boolean;
}

export interface CreateProductionCenterDto {
  name: string;
  description?: string;
  printTicket?: boolean;
  showOnScreen?: boolean;
  printerConfigurationId?: string;
  sortOrder?: number;
}

export interface UpdateProductionCenterDto {
  name: string;
  description?: string;
  printTicket: boolean;
  showOnScreen: boolean;
  printerConfigurationId?: string;
  isActive: boolean;
  sortOrder: number;
}

export const productionCenterApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all production centers
    getProductionCenters: builder.query<ProductionCenter[], void>({
      query: () => '/productioncenter',
      providesTags: (result) => [
        { type: 'ProductionCenter' as const, id: 'LIST' },
        ...(result || []).map(({ id }) => ({ type: 'ProductionCenter' as const, id })),
      ],
    }),

    // Get a single production center by ID
    getProductionCenter: builder.query<ProductionCenter, string>({
      query: (id) => `/productioncenter/${id}`,
      providesTags: (result, error, id) => [
        { type: 'ProductionCenter' as const, id },
      ],
    }),

    // Create a new production center
    createProductionCenter: builder.mutation<ProductionCenter, CreateProductionCenterDto>({
      query: (data) => ({
        url: '/productioncenter',
        method: 'POST',
        body: data,
      }),
      // Invalida también ACTIVE, KITCHEN y EXISTS: un centro nuevo nace activo,
      // así que los selectores de "centros activos" (el del modal de producto,
      // por ejemplo) tienen que verlo de inmediato. Invalidando solo LIST, el
      // usuario creaba un centro y no aparecía en el desplegable desde el que
      // acababa de crearlo.
      invalidatesTags: [
        { type: 'ProductionCenter' as const, id: 'LIST' },
        { type: 'ProductionCenter' as const, id: 'ACTIVE' },
        { type: 'ProductionCenter' as const, id: 'KITCHEN' },
        { type: 'ProductionCenter' as const, id: 'EXISTS' },
      ],
    }),

    // Update a production center
    updateProductionCenter: builder.mutation<ProductionCenter, { id: string; data: UpdateProductionCenterDto }>({
      query: ({ id, data }) => ({
        url: `/productioncenter/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ProductionCenter' as const, id },
        { type: 'ProductionCenter' as const, id: 'LIST' },
        // Editar puede activar/desactivar o cambiar showOnScreen.
        { type: 'ProductionCenter' as const, id: 'ACTIVE' },
        { type: 'ProductionCenter' as const, id: 'KITCHEN' },
      ],
    }),

    // Delete a production center
    deleteProductionCenter: builder.mutation<void, string>({
      query: (id) => ({
        url: `/productioncenter/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'ProductionCenter' as const, id: 'LIST' },
        { type: 'ProductionCenter' as const, id: 'ACTIVE' },
        { type: 'ProductionCenter' as const, id: 'KITCHEN' },
        { type: 'ProductionCenter' as const, id: 'EXISTS' },
      ],
    }),

    // Get production centers for kitchen display (only those with showOnScreen = true)
    getProductionCentersForKitchenDisplay: builder.query<ProductionCenterSimple[], void>({
      query: () => '/productioncenter/kitchen-display',
      providesTags: [{ type: 'ProductionCenter' as const, id: 'KITCHEN' }],
    }),

    // Get all active production centers (for dropdowns)
    getActiveProductionCenters: builder.query<ProductionCenterSimple[], void>({
      query: () => '/productioncenter/active',
      providesTags: [{ type: 'ProductionCenter' as const, id: 'ACTIVE' }],
    }),

    // Check if any production centers exist
    hasProductionCenters: builder.query<{ hasProductionCenters: boolean }, void>({
      query: () => '/productioncenter/exists',
      providesTags: [{ type: 'ProductionCenter' as const, id: 'EXISTS' }],
      transformResponse: (response: boolean) => ({ hasProductionCenters: response }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProductionCentersQuery,
  useGetProductionCenterQuery,
  useCreateProductionCenterMutation,
  useUpdateProductionCenterMutation,
  useDeleteProductionCenterMutation,
  useGetProductionCentersForKitchenDisplayQuery,
  useGetActiveProductionCentersQuery,
  useHasProductionCentersQuery,
} = productionCenterApi;
